import React from 'react';
import cn from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { applyClasses, applyProps } from '../shared/js';
import { Button, Icons, Typography, Accordion, Form } from '..';

import styles from './packages.module.scss';

const Card = ({
  id,
  elementId,
  title,
  price,
  onSelect,
  description,
  coverage,
  monthly,
  ...props
}) => {
  const { formatMessage, messages, locale } = useIntl();

  const formatFeature = (feature) => {
    const messageId = messages[`packages.coverage.${feature.name}`]
      ? `packages.coverage.${feature.name}`
      : `packages.coverage.${feature.defaultName}`;
    const value = feature.terms?.valueAsString || 'Full';
    let description;
    let defaultMessage = `packages.coverage.${feature.name}`;
    let values = feature.values || {
      amount: formatMessage({
        id: `packages.amounts.${value}`,
        defaultMessage: value,
      }),
    };

    return (
      <FormattedMessage
        id={messageId}
        description={description}
        defaultMessage={defaultMessage}
        values={values}
      />
    );
  };

  const handleSelect = () => {
    onSelect && onSelect(id);
  };

  return (
    <>
      <div
        id={elementId}
        className={cn(
          styles.packageCard,
          // eslint-disable-next-line react/prop-types
          props.className,
          applyClasses(props, styles)
        )}
        {...applyProps(props)}
      >
        {props.primary && (
          <span className={styles.bestValue}>
            <FormattedMessage
              id="packages.bestValue"
              description="Best value label for a package"
              defaultMessage="Our best value"
            />
          </span>
        )}
        <Typography h3 center className={styles.title}>
          {title}
        </Typography>
        <div className={styles.price}>
          <span className={styles.dollars}>
            {locale === 'en' && <span className={styles.dollarSign}>$</span>}
            {`${price}`.split('.')[0]}
            <span className={styles.cents}>{`${price}`.split('.')[1]}</span>
            {locale === 'fr' && (
              <span className={cn(styles.dollarSign, styles.french)}>$</span>
            )}
          </span>
        </div>
        <div className={styles.frequency}>
          {monthly ? (
            <FormattedMessage
              id="packages.monthly"
              description="Monthly package payments"
              defaultMessage="monthly"
            />
          ) : (
            <FormattedMessage
              id="packages.yearly"
              description="Yearly package payments"
              defaultMessage="yearly"
            />
          )}
        </div>
        <Button
          id={elementId && `${elementId}_button`}
          primary
          fullWidth
          fixedHeight
          onClick={handleSelect}
          className={styles.button}
        >
          <FormattedMessage
            id="packages.view"
            description="View and customize button text"
            defaultMessage="View and customize"
          />
        </Button>
        <div className={styles.coverage}>
          <p className={styles.description}>{description}</p>
          <ul className={styles.features}>
            {coverage.map((feature, index) => (
              <li key={index} className={styles.feature}>
                {feature.content ? (
                  feature.content
                ) : (
                  <>
                    {feature.showRental ? (
                      <Icons.AutoPlus />
                    ) : (
                      <Icons.Checkmark />
                    )}
                    {formatFeature(feature)}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <Form.Section className={styles.card}>
        <div className={cn(styles.packageCard, styles.mobile)}>
          {props.primary && (
            <span className={styles.bestValue}>
              <FormattedMessage
                id="packages.bestValue"
                description="Best value label for a package"
                defaultMessage="Our best value"
              />
            </span>
          )}
          <Accordion className={styles.accordion}>
            <Accordion.Toggle
              className={styles.toggle}
              openClassName={styles.openToggle}
              id={id}
            >
              <div className={cn(styles.mobile, styles.mobileToggle)}>
                <div className={styles.mobilePrice}>
                  <div className={styles.price}>
                    <div className={styles.amount}>
                      <span className={styles.dollars}>
                        {locale === 'en' && (
                          <span className={styles.dollarSign}>$</span>
                        )}
                        {`${price}`.split('.')[0]}
                        <span className={styles.cents}>
                          {`${price}`.split('.')[1]}
                        </span>
                        {locale === 'fr' && (
                          <span
                            className={cn(
                              styles.dollarSign,
                              styles.dollarSign.french
                            )}
                          >
                            $
                          </span>
                        )}
                      </span>
                      <div className={styles.frequency}>
                        {monthly ? (
                          <FormattedMessage
                            id="packages.monthly"
                            description="Monthly package payments"
                            defaultMessage="monthly"
                          />
                        ) : (
                          <FormattedMessage
                            id="packages.yearly"
                            description="Yearly package payments"
                            defaultMessage="yearly"
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <Typography h3 center className={styles.title}>
                  {title}
                </Typography>
                <Icons.Chevron className={styles.chevron} tertiary />
              </div>
            </Accordion.Toggle>
            <Accordion.Content openClassName={styles.openContent}>
              <div className={styles.coverage}>
                <p className={styles.description}>{description}</p>
                <ul className={styles.features}>
                  {coverage.map((feature, index) => (
                    <li key={index} className={styles.feature}>
                      {feature.content
                        ? feature.content
                        : formatFeature(feature)}
                    </li>
                  ))}
                </ul>
                <Button
                  id={elementId && `${elementId}_button_mobile`}
                  primary
                  mtSm
                  fullWidth
                  onClick={handleSelect}
                  className={styles.button}
                >
                  <FormattedMessage
                    id="packages.view"
                    description="View and customize button text"
                    defaultMessage="View and customize"
                  />
                </Button>
              </div>
            </Accordion.Content>
          </Accordion>
        </div>
      </Form.Section>
    </>
  );
};

Card.propTypes = {
  /** The ID of the card element. Useful to select the card during testing. */
  elementId: PropTypes.string,
  /** The guid of the package */
  id: PropTypes.string,
  /** The title of the package */
  title: PropTypes.string,
  /** The price of the package */
  price: PropTypes.string,
  /** Callback function when the view button is pressed */
  onSelect: PropTypes.func,
  /** The short package description */
  description: PropTypes.string,
  /** The list of the package's coverage features */
  coverage: PropTypes.arrayOf(PropTypes.object),
  /** Adds the best value tag, increased size and box shadow to the card */
  primary: PropTypes.bool,
  /** A smaller, less prominent card to display alongside a primary card */
  secondary: PropTypes.bool,
  /** Displays the yearly label underneath the price */
  yearly: PropTypes.bool,
  /** Displays the monthly label underneath the price */
  monthly: PropTypes.bool,
};

Card.defaultProps = {
  id: undefined,
  title: undefined,
  price: '0.00',
  onSelect: undefined,
  description: undefined,
  coverage: [],
  primary: false,
  secondary: true,
  yearly: false,
  monthly: true,
};

export default Card;
